import { lazy } from "react";

const RELOAD_KEY = "lazyWithRetry:reloadedAt";
const RELOAD_DEBOUNCE_MS = 10_000;
// If reload() doesn't actually navigate (canceled by beforeunload, BFCache,
// extension), fall back to ChunkErrorBoundary instead of hanging Suspense.
const RELOAD_FALLBACK_MS = 5_000;

const isChunkFetchError = (err) => {
  const msg = err?.message || "";
  return (
    err?.name === "ChunkLoadError" ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    msg.includes("is not a valid JavaScript MIME type")
  );
};

// Storage may throw in Safari private mode, sandboxed iframes, or when
// quota is exhausted. Treat failure as "no record" / "couldn't record".
const readReloadStamp = () => {
  try {
    return Number(sessionStorage.getItem(RELOAD_KEY) || 0);
  } catch {
    return 0;
  }
};
const writeReloadStamp = (value) => {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(value));
  } catch {
    // Without a stamp we lose the reload-loop guard, but a single reload
    // still recovers the common case. Accept it.
  }
};

// Wraps React.lazy so a stale `index.html` (whose chunk hashes no longer
// exist on the CDN after a deploy) recovers via a single hard reload
// instead of crashing into the error boundary. Debounced via sessionStorage
// to prevent reload loops if the CDN really is broken.
export default function lazyWithRetry(importer) {
  return lazy(() =>
    importer().catch((err) => {
      if (!isChunkFetchError(err)) throw err;

      const last = readReloadStamp();
      if (Date.now() - last < RELOAD_DEBOUNCE_MS) throw err;

      writeReloadStamp(Date.now());
      window.location.reload();
      return new Promise((_, reject) => {
        setTimeout(() => reject(err), RELOAD_FALLBACK_MS);
      });
    }),
  );
}
